// @ts-nocheck
/**
 * Performance optimization utilities for Sigma08 frontend
 * Includes lazy loading, prefetching, and browser-specific optimizations
 * Enhanced for sub-2-second load times with Core Web Vitals monitoring
 */

import { getCLS, getFID, getFCP, getLCP, getTTFB } from 'web-vitals';

// Performance metrics interface
export interface PerformanceMetrics {
  cls: number | null;
  fid: number | null;
  fcp: number | null;
  lcp: number | null;
  ttfb: number | null;
}

// Performance thresholds
export const PERFORMANCE_THRESHOLDS = {
  lcp: 2500, // 2.5s for good LCP
  fid: 100, // 100ms for good FID
  cls: 0.1, // 0.1 for good CLS
  ttfb: 800, // 800ms for good TTFB
  fcp: 1800, // 1.8s for good FCP
};

// Detect Safari/WebKit browser
export const isSafari = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('android');
};

// Detect iOS Safari
export const isIOSSafari = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Apply Safari-specific performance fixes
export const applySafariOptimizations = (): void => {
  if (isSafari() || isIOSSafari()) {
    // Force GPU acceleration for smoother scrolling
    document.documentElement.style.setProperty('-webkit-transform', 'translateZ(0)');

    // Enable momentum scrolling on iOS
    if (isIOSSafari()) {
      document.documentElement.style.setProperty('-webkit-overflow-scrolling', 'touch');
    }

    // Skip prefetching non-existent vendor bundles
    // Resources are bundled by Vite and loaded automatically
  }
};

// Lazy load images with IntersectionObserver
export const lazyLoadImages = (): void => {
  if ('IntersectionObserver' in window) {
    const imageObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          if (img.dataset.src) {
            img.src = img.dataset.src;
            img.removeAttribute('data-src');
            imageObserver.unobserve(img);
          }
        }
      });
    });

    const images = document.querySelectorAll('img[data-src]');
    images.forEach(img => imageObserver.observe(img));
  }
};

// Preload critical fonts
export const preloadFonts = (): void => {
  // IBM Plex fonts are bundled locally via @ibm/plex package in CSS imports
  // Font preloading is handled automatically by the browser with local fonts
};

// Optimize bundle loading with resource hints
export const optimizeBundleLoading = (): void => {
  // DNS prefetch for API domains
  const apiDomains = ['https://api.sigma08.com', '/api'];

  apiDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = domain;
    document.head.appendChild(link);
  });

  // Preconnect to critical domains (Google Fonts removed - using local fonts)
  const criticalDomains: string[] = [];

  criticalDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    document.head.appendChild(link);
  });
};

// Initialize all performance optimizations
export const initializePerformanceOptimizations = (): void => {
  // Apply browser-specific fixes
  applySafariOptimizations();

  // Optimize resource loading
  optimizeBundleLoading();

  // Setup lazy loading
  lazyLoadImages();

  // Preload fonts for better text rendering
  preloadFonts();

  // Log performance metrics in development
  if (import.meta.env.DEV) {
    window.addEventListener('load', () => {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      console.log('Performance metrics:', {
        domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
        loadComplete: perfData.loadEventEnd - perfData.loadEventStart,
        domInteractive: perfData.domInteractive - perfData.fetchStart,
        firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime || 0,
      });
    });
  }
};

// Performance monitoring class
export class PerformanceMonitor {
  private metrics: PerformanceMetrics = {
    cls: null,
    fid: null,
    fcp: null,
    lcp: null,
    ttfb: null,
  };

  constructor() {
    this.initializeMonitoring();
  }

  private initializeMonitoring(): void {
    getCLS(metric => {
      this.metrics.cls = metric.value;
      this.checkThreshold('cls', metric.value);
    });

    getFID(metric => {
      this.metrics.fid = metric.value;
      this.checkThreshold('fid', metric.value);
    });

    getFCP(metric => {
      this.metrics.fcp = metric.value;
      this.checkThreshold('fcp', metric.value);
    });

    getLCP(metric => {
      this.metrics.lcp = metric.value;
      this.checkThreshold('lcp', metric.value);
    });

    getTTFB(metric => {
      this.metrics.ttfb = metric.value;
      this.checkThreshold('ttfb', metric.value);
    });
  }

  private checkThreshold(metric: keyof typeof PERFORMANCE_THRESHOLDS, value: number): void {
    const threshold = PERFORMANCE_THRESHOLDS[metric];
    if (value > threshold) {
      console.warn(
        `Performance warning: ${metric.toUpperCase()} (${value.toFixed(2)}ms) exceeds threshold (${threshold}ms)`
      );
    }
  }

  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }
}

// Export enhanced performance monitoring utilities
export const reportWebVitals = (onPerfEntry?: (metric: any) => void): void => {
  if (onPerfEntry && onPerfEntry instanceof Function) {
    getCLS(onPerfEntry);
    getFID(onPerfEntry);
    getFCP(onPerfEntry);
    getLCP(onPerfEntry);
    getTTFB(onPerfEntry);
  }
};
